/* eslint-disable indent */
import { useEffect, useState } from "react"
import Screen from "src/shared/components/layout/screen/Screen"
import TabMenu from "src/shared/components/navigation/tab-menu/TabMenu"

import "./Checkout.css"
import Estancia from "./sections/estancia/Estancia"
import Resumen from "./sections/resumen/Resumen"
import RoomService from "./sections/room-service/RoomService"
import CommentsIncidences from "./sections/comments-incidences/CommentsIncidences"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { useQuery } from "@apollo/client"
import { EstadosOrdenHistorial, ExtraTarifa, Habitacion, Orden, RentaTarifa, TiposAlojamientos, TiposExtras, useGetHabitacionQuery } from "src/gql/schema"
import { formatCurrency } from "src/shared/hooks/formatCurrency"
import { GET_INCIDENCIAS_ROOM } from "src/pages/home/graphql/queries/incidencias"
import { sum } from "src/shared/helpers/calculator"
import { groupRentasTarifasByTarifa } from "src/shared/helpers/groupRentasTarifasByTarifa"
import { groupExtrasTarifasByTarifa } from "src/shared/helpers/groupExtrasTarifasByTarifa"
import { useIVA } from "src/shared/hooks/useIVA"
import { useDispatch } from "react-redux"
import { toggleRoomDetailsDrawer } from "src/store/navigation/navigationSlice"
import { useDate } from "src/shared/hooks/useDate"
import { useProfile } from "src/shared/hooks/useProfile"
import { useGetEstanciaRows } from "./sections/resumen/hooks/useGetEstanciaRows"

import { tiposPagosValidos, procesarMontos } from "src/pages/easyrewards/utils/AbonarEasyRewards"
import { removeObjectDuplicates } from "src/utils/array"

export type CheckoutTabs = "Estancia" | "RoomService" | "CommentsIncidences"

export const homeTabsList = [
    { label: "Estancia", path: "Estancia", number: 0 },
    { label: "Room Service", path: "RoomService", number: 0 },
    { label: "Comentarios e incidencias", path: "CommentsIncidences", number: 1 },
]

const Checkout = () => {
    const [type, setType] = useState<CheckoutTabs>("Estancia")
    const { diffDays, UTCStringToLocalDate } = useDate()

    const { habitacion_id} = useParams()
    const { usuario_id } = useProfile()

    const dispatch = useDispatch()
    const { getIVA } = useIVA()
    const navigate = useNavigate()
    const location = useLocation()

    const { data } = useGetHabitacionQuery({
        variables: { habitacion_id: habitacion_id || "", usuario_id: usuario_id },
    })

    const reserva = data?.habitacion?.ultima_renta?.reserva

    const [comentarios, setComentarios] = useState<any>([])

    const { data: incidencias } = useQuery(GET_INCIDENCIAS_ROOM, {
        variables: {
            habitacion_id,
        },
    })

    const propinasEstancia = (data?.habitacion?.ultima_renta?.propinas ?? []).reduce(
        (total, p) => total + (p?.total || 0),
        0
    )
    const propinasRS = (data?.habitacion?.ultima_renta?.ordenes ?? []).reduce(
        (total, orden) => total + (orden?.propina?.total || 0),
        0
    )

    const personasExtra = (data?.habitacion?.ultima_renta?.extras ?? []).filter((i) => i.tipo_extra === TiposExtras.Persona)
    const montoPersonasExtra = personasExtra.reduce((total, i) => total + (i?.monto_extra || 0), 0)

    const pagosList = useGetEstanciaRows(data?.habitacion as Habitacion)

    useEffect(() => {
        setComentarios(data?.habitacion?.ultima_renta?.comentarios)
    }, [data])

    const obtenerTotalOrden = (orden: any) => {
        return orden.total_con_iva || 0
    }

    const obtenerTotalPagosValidos = (orden: any) => {
        const pagosValidos = orden.pago?.detalles_pago?.filter((detalle) =>
            tiposPagosValidos.includes(detalle.tipo_pago)
        )
        if (pagosValidos && pagosValidos.length > 0) {
            return pagosValidos.reduce((acc, detalle) => {
                const subtotal = detalle.subtotal || 0
                return acc + subtotal
            }, 0)
        }
        return 0
    }

    const obtenerTotalPorCategoria = (orden: any, categoria: string) => {
        return (
            orden.detalles_orden
                ?.flatMap((detalle) => {
                    const categoriaArticulo = detalle.almacen_articulo?.articulo?.categoria_articulo?.nombre
                    const precioArticulo = detalle.almacen_articulo?.precio || 0
                    if (categoriaArticulo === categoria) {
                        return precioArticulo * detalle.cantidad
                    }
                    return 0
                })
                .reduce((acc, precio) => acc + precio, 0) || 0
        )
    }

    const obtenerMontosPorOrden = (orden: any) => {
        return {
            totalBebidas: obtenerTotalPorCategoria(orden, "Bebidas"),
            totalAlimentos: obtenerTotalPorCategoria(orden, "Alimentos"),
            totalSexAndSpa: obtenerTotalPorCategoria(orden, "Sex & Spa"),
            totalPagosInvalidos: obtenerTotalOrden(orden) - obtenerTotalPagosValidos(orden),
            totalPagosValidos: obtenerTotalPagosValidos(orden),
        }
    }

    const procesarOrdenes = (ordenes: any[]) => {
        return ordenes.reduce(
            (acc, orden) => {
                const { totalBebidas, totalAlimentos, totalSexAndSpa, totalPagosInvalidos, totalPagosValidos } =
                    obtenerMontosPorOrden(orden)
                const { montoBebidas, montoAlimentos, montoSexAndSpa } = procesarMontos(
                    totalPagosValidos,
                    totalPagosInvalidos,
                    totalBebidas,
                    totalAlimentos,
                    totalSexAndSpa
                )
                acc.montoTotalBebidas += montoBebidas
                acc.montoTotalAlimentos += montoAlimentos
                acc.montoTotalSexAndSpa += montoSexAndSpa

                return acc
            },
            { montoTotalBebidas: 0, montoTotalAlimentos: 0, montoTotalSexAndSpa: 0 }
        )
    }
    const ordenes = data?.habitacion?.ultima_renta?.ordenes?.filter((i)=>i?.estado_orden !== EstadosOrdenHistorial.Cancelada) || []
    const { montoTotalBebidas, montoTotalAlimentos, montoTotalSexAndSpa } = procesarOrdenes(ordenes)

    const roomSerice = sum(ordenes.map((o) => o.total_con_iva || 0))
    const totalCheckInAnticipado = data?.habitacion?.ultima_renta?.subtotales?.total_early_checkin || 0
    const hospedajeExtras = (data?.habitacion?.ultima_renta?.extras ?? []).filter((e) => e.tipo_extra === TiposExtras.Hospedaje)
    const cantidadHospedajeExtras = [...new Map(
        hospedajeExtras.map((e) => [e.extra_id, e])
        ).values()].reduce(
        (total, extra) => total + (extra.numero ?? 0),
        0
        )
    const montoTotalHospedajesExtra = sum(
        hospedajeExtras.flatMap((e) => groupExtrasTarifasByTarifa(e.extras_tarifas as ExtraTarifa[] || []).map((g) => g.total))
    )
    const horasExtras = (data?.habitacion?.ultima_renta?.extras ?? []).filter((e) => e.tipo_extra === TiposExtras.Hora)
    const montoTotalHorasExtra = sum(
        horasExtras.flatMap((e) => groupExtrasTarifasByTarifa(e.extras_tarifas as ExtraTarifa[] || []).map((g) => g.total))
    )
    const totalEstancia = sum(groupRentasTarifasByTarifa(data?.habitacion?.ultima_renta?.rentas_tarifas as RentaTarifa[] || []).map((rt) => rt.total))
    const totalImpuestos = getIVA(sum([totalEstancia, montoTotalHospedajesExtra, montoPersonasExtra, montoTotalHorasExtra, roomSerice, totalCheckInAnticipado]))

    //Se agregó un filtro para limpiar los elementos pago_id repetidos
    const pagos = removeObjectDuplicates([...(data?.habitacion?.ultima_renta?.pagos || []),  ...(reserva?.pagos || [])], "pago_id")

    return (
        <Screen
            title={`Checkout ${
                data?.habitacion.tipo_habitacion?.nombre ? data?.habitacion.tipo_habitacion?.nombre : ""
            } ${data?.habitacion.numero_habitacion ? data?.habitacion.numero_habitacion : ""}`}
            close
            onClose={() => {
                const origenPagoPendiente = location.state?.origen || "Home"
                origenPagoPendiente === "pagoPendiente" ? navigate(`/u`) : navigate(-1)
                dispatch(toggleRoomDetailsDrawer(false))
            }}
        >
            <div className="room-detail__page">
                <div className="room-detail__page--checkout-main">
                    <TabMenu
                        value={type}
                        onChange={(value) => setType(value as CheckoutTabs)}
                        tabList={[
                            { label: "Estancia", path: "Estancia", number: 0 },
                            { label: "Room Service", path: "RoomService", number: 0 },
                            {
                                label: "Comentarios e incidencias",
                                path: "CommentsIncidences",
                                number: incidencias?.incidencias?.length,
                            },
                        ]}
                        style={{ margin: "20px 0" }}
                    />

                    {type === "Estancia" ? (
                        <Estancia
                            habitacion={data?.habitacion as Habitacion}
                            pagosList={pagosList}
                            total={formatCurrency(sum([totalEstancia, montoTotalHospedajesExtra, montoPersonasExtra, montoTotalHorasExtra, totalCheckInAnticipado]))}
                        />
                    ) : type === "RoomService" ? (
                        <RoomService ordenes={ordenes as Orden[]} />
                    ) : type === "CommentsIncidences" ? (
                        <CommentsIncidences
                            comentarios={comentarios}
                            habitacionId={habitacion_id || ""}
                            incidencias={incidencias?.incidencias}
                        />
                    ) : (
                        <></>
                    )}
                </div>
                <Resumen
                    montoTotalBebidas={montoTotalBebidas}
                    montoTotalAlimentos={montoTotalAlimentos}
                    montoTotalSexAndSpa={montoTotalSexAndSpa}
                    tipoAlojamiento={data?.habitacion?.ultima_renta?.tipo_alojamiento as TiposAlojamientos}
                    numOrdenesRoomService={ordenes?.length || 0}
                    totalCheckInAnticipado={totalCheckInAnticipado}
                    propina={sum([propinasEstancia, propinasRS])}
                    roomDays={
                        diffDays(
                            UTCStringToLocalDate(data?.habitacion?.ultima_renta?.fecha_registro),
                            UTCStringToLocalDate(data?.habitacion?.ultima_renta?.fecha_fin)
                        ) || 1
                    }
                    personasExtra={personasExtra.reduce((total, i) => total + (i?.numero || 0), 0)}
                    montoPersonasExtra={montoPersonasExtra}
                    hospedajesExtra={cantidadHospedajeExtras}
                    montoTotalHospedajesExtra={montoTotalHospedajesExtra}
                    habitacion_id={habitacion_id || ""}
                    habitacion={data?.habitacion as Habitacion}
                    totalEstancia={totalEstancia}
                    totalImpuestos={totalImpuestos}
                    roomSerice={roomSerice}
                    impuestoRoomService={getIVA(roomSerice)}
                    totalPagado={sum(pagos.flatMap(
                        (pago) => pago.detalles_pago?.flatMap((d) => d.subtotal || [0]) || [0]
                    ))}
                    pagosPendientes={sum([
                        sum(
                            (data?.habitacion?.ultima_renta?.extras?.filter((e) => !e.fecha_pago) || [])
                                .map((ex) => ex.monto_extra || 0)
                        ),
                        data?.habitacion?.ultima_renta?.saldo_pendiente_estancia || 0,
                    ])}
                />
            </div>
        </Screen>
    )
}

export default Checkout
